import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import LineIcon from '../Icons/LineIcon';
import { cc } from '../../modules/styles';

import './ModalCloseButton.css';

function ModalCloseButton({
  onCloseRequest,
  shouldHide = false,
  showRequest = 0,
}) {
  const [isVisible, setIsVisible] = useState(true);
  const timerRef = useRef(null);
  const isHoveredRef = useRef(false);

  const showTemporarily = useCallback(() => {
    setIsVisible(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (!isHoveredRef.current) {
        setIsVisible(false);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    if (shouldHide) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      setIsVisible(false);
    } else {
      showTemporarily();
    }
  }, [shouldHide, showTemporarily]);

  // Explicit show requests take priority, even if another gallery image stays zoomed.
  useEffect(() => {
    if (showRequest > 0) {
      showTemporarily();
    }
  }, [showRequest, showTemporarily]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  function handleMouseEnter() {
    isHoveredRef.current = true;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setIsVisible(true);
  }

  function handleMouseLeave() {
    isHoveredRef.current = false;
    if (shouldHide) {
      setIsVisible(false);
    } else {
      showTemporarily();
    }
  }

  return (
    <button
      className={cc(
        'ModalCloseButton reset-button',
        isVisible ? 'visible' : ''
      )}
      type="button"
      onClick={event => {
        event.stopPropagation();
        onCloseRequest();
      }}
      aria-label="Close image"
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      disabled={!isVisible}
      title="Close image"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LineIcon type="cross" width={20} height={20} thickness={2} />
    </button>
  );
}

ModalCloseButton.propTypes = {
  onCloseRequest: PropTypes.func.isRequired,
  shouldHide: PropTypes.bool,
  showRequest: PropTypes.number,
};

export default ModalCloseButton;
